import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Privacy Policy"
        description="The privacy policy for Meshly (Meshly Labs, Inc.), the AI verification layer for the systems your business runs on."
        canonicalUrl="https://www.meshly.ai/privacy"
      />
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 hover:bg-primary/10"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12">
            <CardContent className="prose prose-lg max-w-none space-y-6">
              <h1 className="text-3xl md:text-4xl font-bold mb-8">Meshly Marketing Privacy Policy</h1>

              <p className="text-sm text-muted-foreground font-medium mb-6">
                Last updated: April 7, 2026
              </p>

              <p className="text-sm text-muted-foreground font-medium mb-6">
                Applies to: meshly.ai website, newsletter signups, and outbound marketing
              </p>

              {/* Section 1 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">1. Who We Are</h2>
                <p className="text-base text-muted-foreground">
                  Meshly, Inc. ("Meshly," "we," "us") provides a revenue integrity platform for businesses. Our principal office is in California, USA.
                </p>
                <p className="text-base text-muted-foreground">
                  Contact:{" "}
                  <a href="mailto:privacy@meshly.ai" className="text-primary hover:underline">
                    privacy@meshly.ai
                  </a>
                </p>
                <p className="text-base text-muted-foreground italic">
                  This policy covers privacy practices for the meshly.ai public website, newsletter signups, and outbound marketing activities. If you are a Meshly platform customer with connected business systems, our Platform Privacy Policy governs how we handle your platform data.
                </p>
              </section>

              {/* Section 2 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">2. What We Collect</h2>

                <h3 className="text-xl font-semibold mt-6">2.1 Website Visitors</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-muted-foreground border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Data Category</th>
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Examples</th>
                        <th className="text-left py-2 font-semibold text-foreground">Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Device and browser data</td>
                        <td className="py-2 pr-4">IP address, browser type, operating system</td>
                        <td className="py-2">Security, troubleshooting</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Page visits</td>
                        <td className="py-2 pr-4">URLs visited, referrer, timestamps</td>
                        <td className="py-2">Website analytics (aggregated)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="text-xl font-semibold mt-6">2.2 Newsletter and Marketing Signups</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-muted-foreground border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Data Category</th>
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Examples</th>
                        <th className="text-left py-2 font-semibold text-foreground">Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Contact information</td>
                        <td className="py-2 pr-4">Email address, name (if provided), company (if provided)</td>
                        <td className="py-2">Marketing communications (consent/opt-in)</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Engagement data</td>
                        <td className="py-2 pr-4">Email opens, link clicks</td>
                        <td className="py-2">Campaign optimization</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="text-xl font-semibold mt-6">2.3 Account Registration</h3>
                <p className="text-base text-muted-foreground">
                  If you create a Meshly account, you provide your name, email, company name, and job title. Once you begin using the Meshly platform (connecting business systems, processing documents), our Platform Privacy Policy applies to that data.
                </p>

                <h3 className="text-xl font-semibold mt-6">2.4 Cookies and Tracking Technologies</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-muted-foreground border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Type</th>
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Purpose</th>
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Technologies</th>
                        <th className="text-left py-2 font-semibold text-foreground">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Strictly necessary</td>
                        <td className="py-2 pr-4">Session management, authentication, security</td>
                        <td className="py-2 pr-4">Session cookies, CSRF tokens</td>
                        <td className="py-2">Session or up to 24 hours</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Analytics</td>
                        <td className="py-2 pr-4">Understanding website usage in aggregate</td>
                        <td className="py-2 pr-4">Page view counts, referrer data</td>
                        <td className="py-2">Up to 26 months</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-base text-muted-foreground">
                  Meshly does not use advertising cookies, retargeting pixels, or cross-site tracking technologies. We do not participate in ad networks or sell cookie data.
                </p>
                <p className="text-base text-muted-foreground">
                  <strong className="text-foreground">How to control cookies:</strong> You can block or delete cookies through your browser settings. Meshly honors Global Privacy Control (GPC) browser signals. Blocking strictly necessary cookies may prevent the website from functioning correctly.
                </p>
              </section>

              {/* Section 3 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">3. How We Use Your Information</h2>
                <ol className="list-decimal pl-6 space-y-2">
                  <li className="text-base text-muted-foreground">
                    <strong className="text-foreground">Website operation</strong> — serving pages, maintaining security, troubleshooting.
                  </li>
                  <li className="text-base text-muted-foreground">
                    <strong className="text-foreground">Analytics</strong> — understanding aggregate usage patterns. No individual behavioral profiles.
                  </li>
                  <li className="text-base text-muted-foreground">
                    <strong className="text-foreground">Marketing communications</strong> — newsletters, product updates, event invitations to opt-in individuals.
                  </li>
                  <li className="text-base text-muted-foreground">
                    <strong className="text-foreground">Legal compliance</strong> — responding to lawful requests, enforcing our terms.
                  </li>
                </ol>
                <p className="text-base text-muted-foreground">
                  <strong className="text-foreground">We do not:</strong> sell personal information, share for cross-context behavioral advertising, use advertising cookies or retargeting pixels, purchase or rent email lists.
                </p>
              </section>

              {/* Section 4 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">4. Marketing Communications</h2>

                <h3 className="text-xl font-semibold mt-6">4.1 Opt-In</h3>
                <p className="text-base text-muted-foreground">
                  We send marketing communications only to individuals who have affirmatively opted in. Pre-checked consent boxes are never used.
                </p>

                <h3 className="text-xl font-semibold mt-6">4.2 Opt-Out</h3>
                <p className="text-base text-muted-foreground">
                  Every marketing email includes an unsubscribe link. Opt-out requests are honored within 10 business days per CAN-SPAM. Opting out of marketing does not affect transactional communications.
                </p>

                <h3 className="text-xl font-semibold mt-6">4.3 CAN-SPAM Compliance</h3>
                <p className="text-base text-muted-foreground">
                  All marketing emails are clearly identified as marketing, include our physical mailing address, contain a working unsubscribe mechanism, use accurate headers, and are not sent to purchased lists.
                </p>
              </section>

              {/* Section 5 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">5. How We Share Your Information</h2>

                <h3 className="text-xl font-semibold mt-6">5.1 Service Providers</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-muted-foreground border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Provider</th>
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Service</th>
                        <th className="text-left py-2 font-semibold text-foreground">Data Shared</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Vercel</td>
                        <td className="py-2 pr-4">Frontend hosting and CDN</td>
                        <td className="py-2">Website usage data, IP addresses</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Email service provider</td>
                        <td className="py-2 pr-4">Newsletter delivery</td>
                        <td className="py-2">Subscriber email addresses, engagement data</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-base text-muted-foreground">
                  All service provider agreements include CCPA/CPRA-compliant data protection terms.
                </p>

                <h3 className="text-xl font-semibold mt-6">5.2 Legal Requirements</h3>
                <p className="text-base text-muted-foreground">
                  We may disclose information when required by law. We will notify you before disclosure unless legally prohibited from doing so.
                </p>

                <h3 className="text-xl font-semibold mt-6">5.3 Business Transfers</h3>
                <p className="text-base text-muted-foreground">
                  In the event of a merger or acquisition, we will notify you before your information becomes subject to a different privacy policy.
                </p>
                <p className="text-base text-muted-foreground font-medium">
                  We do not sell personal information. We have not sold personal information in the preceding 12 months.
                </p>
              </section>

              {/* Section 6 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">6. Your Privacy Rights</h2>
                <p className="text-base text-muted-foreground">
                  The following rights are available under CCPA/CPRA, VCDPA, CPA, CTDPA, TDPSA, OCPA, and other state privacy laws. We extend these rights to all users regardless of state of residence.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-muted-foreground border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Right</th>
                        <th className="text-left py-2 font-semibold text-foreground">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Right to Know</td>
                        <td className="py-2">Request disclosure of personal information collected</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Right to Delete</td>
                        <td className="py-2">Request deletion of personal information</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Right to Correct</td>
                        <td className="py-2">Request correction of inaccurate information</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Right to Opt Out</td>
                        <td className="py-2">We don't sell or share for behavioral advertising — nothing to opt out of</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Right to Non-Discrimination</td>
                        <td className="py-2">We will not discriminate against you for exercising your rights</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-base text-muted-foreground">
                  <strong className="text-foreground">How to exercise your rights:</strong> Email{" "}
                  <a href="mailto:privacy@meshly.ai" className="text-primary hover:underline">
                    privacy@meshly.ai
                  </a>
                  . We will verify your identity and respond within 45 days.
                </p>
                <p className="text-base text-muted-foreground">
                  <strong className="text-foreground">Authorized agents:</strong> Written authorization is required.
                </p>
                <p className="text-base text-muted-foreground">
                  <strong className="text-foreground">Global Privacy Control (GPC):</strong> Honored as a valid opt-out signal under CCPA/CPRA.
                </p>
              </section>

              {/* Section 7 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">7. Data Retention</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-muted-foreground border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Data Type</th>
                        <th className="text-left py-2 font-semibold text-foreground">Retention Period</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Newsletter subscriber email</td>
                        <td className="py-2">Until unsubscribe + 30 days</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Website analytics</td>
                        <td className="py-2">Aggregated, 26 months</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4">Marketing campaign engagement data</td>
                        <td className="py-2">2 years after last engagement</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Section 8 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">8. Account Closure and Data Deletion</h2>
                <p className="text-base text-muted-foreground">
                  You may request closure of your account at any time, regardless of subscription tier or contract status. Account closure requests are processed within 7 business days. Personal data is permanently deleted within 30 days after closure, except where retention is required by law.
                </p>
              </section>

              {/* Section 9 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">9. Children's Privacy</h2>
                <p className="text-base text-muted-foreground">
                  Our services are not directed to children under 13. We do not knowingly collect personal information from children. If we discover that we have collected personal data from a child, we will delete it promptly.
                </p>
              </section>

              {/* Section 10 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">10. International Data Transfers</h2>
                <p className="text-base text-muted-foreground">
                  Personal information is processed in the United States via Google Cloud Platform.
                </p>
              </section>

              {/* Section 11 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">11. Changes to This Policy</h2>
                <p className="text-base text-muted-foreground">
                  This policy is updated when our practices change. Material changes will be posted prominently on meshly.ai.
                </p>
              </section>

              {/* Section 12 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold mt-8">12. Contact</h2>
                <p className="text-base text-muted-foreground">
                  <a href="mailto:privacy@meshly.ai" className="text-primary hover:underline">
                    privacy@meshly.ai
                  </a>
                </p>
              </section>

              <hr className="my-8 border-muted" />

              <p className="text-sm text-muted-foreground text-center">
                Meshly, Inc. —{" "}
                <a href="https://www.meshly.ai" className="text-primary hover:underline">
                  meshly.ai
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Privacy;
